var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ScriptFacet, RenderVisibilityMessage, RoomChangedMessage, RPCScriptMessage, RPCScriptReturnMessage, AskErrorMessage, AskOkMessage, ControllerAxisMessage, ControllerButtonMessage, ChangeRoomSupervisionMessage, ScriptComposeFacet, LayoutUpdatedMessage, hashCode, MetaDataFacet, PersistentDataFacet, LayoutCompleteMessage, TrainingVideoStateChangedMessage, TrainingStageChangedMessage, TrainingFinishedMessage, AnimationClipFinishedMessage } from "flavr-common";
import { ModelClickedMessage, PickupGrabbableMessage, ReleaseGrabbableMessage, RemoteModelClickedMessage } from "@messages/interaction/InteractionMessages";
import { RequestPredefinedModelScriptMessage, AwaitScriptsActivationRequestMessage, ResetModelScriptsRequestMessage, ResponsePredefinedScriptMessage, ScriptChangedMessage, ScriptRPCMessage, ScriptRPCOnlyHostMessage } from "@messages/scripting/ScriptMessages";
import { ModelSelectMessage } from "@messages/selection/SelectionStrategyMessages";
import { OnEnterContactsMessage, OnLeaveContactsMessage } from "@messages/selection/CollisionShapeMessages";
import { OnDropModelMessage, OnPickupModelMessage } from "@messages/attachment/AttachmentMessage";
import { UserSpawnedMessage } from "@messages/session/SessionMessages";
import { AvatarPeerRoomChangedMessage, PeerAddedMessage, PeerRemovedMessage } from "@messages/session/PeerMessages";
import { InputFlaVRDefaultActionMessage, InputFlaVRDefaultActionType } from "@messages/input/InputMessages";
import { WebXRPresentingMessage } from "@messages/xr/WebXRMessages";
import { ControllerConnectedMessage } from "@messages/controller";
import { FacetRemoveHandler, FacetHandler, MessageHandler, ModelEventHandler, ModelEventType, NewModule, MessageRequestHandler } from "@modules/module";
import { modelPool } from "@modules/model";
import { Matrix4 } from "flavr-math";
import { PREDEFINED_MODEL_SCRIPT } from "./PredefinedModelScript";
import { ScriptContext } from "./ScriptContext";
import { scriptService } from "../services/ScriptService";
import { uiBusService } from "../services/UIBusService";
import { usersService } from "@modules/multiplayer/UsersService";
import { peerService } from "@modules/multiplayer/peer";
/**
 * Hosts runtime model scripts so scripted behaviours can react to gameplay events, RPC calls, room changes and model lifecycle updates without being compiled into dedicated modules.
 *
 * Facets:
 * - `ScriptFacet` and `ScriptComposeFacet` - Define which scripts should exist on a model and with which properties they should be instantiated.
 * - `PersistentDataFacet` and `MetaDataFacet` - Provide script-visible model metadata and persistent state updates.
 *
 * Messages:
 * - `RequestPredefinedModelScriptMessage`, `AwaitScriptsActivationRequestMessage`, `ResetModelScriptsRequestMessage`, `RPCScriptMessage`, `ScriptRPCMessage` and `ScriptRPCOnlyHostMessage` - Load, activate, reset and call scripts through request/response and fire-and-forget RPC flows.
 * - `ModelSelectMessage`, `ModelClickedMessage`, `RemoteModelClickedMessage`, `ControllerButtonMessage`, `ControllerAxisMessage`, `InputFlaVRDefaultActionMessage`, `RoomChangedMessage`, `WebXRPresentingMessage`, `OnPickupModelMessage`, `OnDropModelMessage`, `PickupGrabbableMessage` and `ReleaseGrabbableMessage` - Forward interaction, movement and presentation events into active scripts.
 * - `UserSpawnedMessage`, `PeerAddedMessage`, `PeerRemovedMessage`, `RenderVisibilityMessage`, `LayoutCompleteMessage`, `LayoutUpdatedMessage`, `TrainingVideoStateChangedMessage`, `TrainingStageChangedMessage` and `TrainingFinishedMessage` - Notify scripts about session, layout and training state changes.
 *
 * Events:
 * - `ModelEventType.MODEL_DESTROYED` and `ModelEventType.TRANSFORM_UPDATED` - Inform scripts when tracked models are removed or moved.
 */
export class ScriptModule extends NewModule {
    /**
     * Creates the `ScriptContext` that backs this module and makes it available through `scriptService` so other code
     * (e.g. running scripts) can reach it.
     */
    constructor() {
        super("ScriptModule");
        this.scriptIdToHash = new Map();
        this.tempGlobalTransform = new Matrix4();
        this.peersInCurrentRoom = new Map();
        this.context = new ScriptContext(this.connection);
        scriptService.context = this.context;
    }
    /**
     * Stores the URL the app was loaded with on the script context so scripts can read URL parameters, and initializes
     * `uiBusService`.
     */
    init(locationHref) {
        this.context.locationHref = locationHref;
        uiBusService.init();
    }
    /// Script facet handlers
    handleScriptComposeFacet(uid, facet, meta) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.context.updateModelScripts(uid, facet.scripts, meta.timeStamp);
        });
    }
    handleScriptFacet(uid, facet, meta) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.context.updateModelScripts(uid, [
                {
                    scriptId: facet.scriptId,
                    properties: facet.properties,
                    autoExecute: facet.autoExecute,
                    active: facet.active
                }
            ], meta.timeStamp);
        });
    }
    handleRemoveFacetFromModelMessage(uid, _facet, meta) {
        this.context.destroyScripts(uid, meta.timeStamp);
        this.context.flushMessages();
    }
    /// Script message handlers ///
    handleRequestPredefinedModelScriptMessage(_, askId) {
        // ToDo: load predefined script from server / minio?
        this.connection.answer(askId, new ResponsePredefinedScriptMessage(PREDEFINED_MODEL_SCRIPT));
    }
    handleAwaitScriptsActivationRequestMessage(msg, askId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.waitForScriptsCreation(msg.modelUid, msg.includeDescendants, msg.timeoutMs);
                this.activateScriptsLocally(msg.modelUid, msg.includeDescendants);
                yield this.waitForScriptsActivation(msg.modelUid, msg.includeDescendants, msg.timeoutMs);
                this.connection.answer(askId, new AskOkMessage());
            }
            catch (error) {
                const message = error instanceof Error ? error.message : JSON.stringify(error);
                this.connection.answer(askId, new AskErrorMessage(message));
            }
        });
    }
    handleResetModelScriptsRequestMessage(msg, askId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resetScripts = yield this.resetModelScripts(msg.modelUids, msg.includeDescendants);
                yield this.waitForScriptsInitialization(resetScripts, msg.timeoutMs);
                this.context.flushMessages();
                this.connection.answer(askId, new AskOkMessage());
            }
            catch (error) {
                const message = error instanceof Error ? error.message : JSON.stringify(error);
                this.connection.answer(askId, new AskErrorMessage(message));
            }
        });
    }
    handleScriptChangedMessage(msg) {
        this.context.scriptChanged(msg.scriptId);
    }
    handleRPCScriptMessage(msg, askId) {
        this.context.scriptManager.getFunctionScript(msg.scriptId, msg.generated)
            .then((script) => {
            if (script === undefined)
                throw new Error(`Can't find script for scriptId ${msg.scriptId}`);
            const result = script(msg.param);
            if (askId !== undefined)
                this.connection.answer(askId, new RPCScriptReturnMessage(msg.scriptId, result));
        })
            .catch((error) => {
            if (askId === undefined)
                return;
            error = ("message" in error && typeof error.message === "string")
                ? error.message
                : JSON.stringify(error);
            this.connection.answer(askId, new AskErrorMessage(error));
        });
    }
    handleScriptRPCMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.modelUid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            let hash = this.scriptIdToHash.get(script.scriptId);
            if (hash === undefined) {
                hash = hashCode(script.scriptId);
                this.scriptIdToHash.set(script.scriptId, hash);
            }
            if (msg.scriptHash !== hash)
                continue;
            script.onRPC(msg.funcNameHash, ...msg.params);
        }
    }
    handleScriptRPCOnlyHostMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.modelUid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            let hash = this.scriptIdToHash.get(script.scriptId);
            if (hash === undefined) {
                hash = hashCode(script.scriptId);
                this.scriptIdToHash.set(script.scriptId, hash);
            }
            if (msg.scriptHash !== hash)
                continue;
            script.onRPC(msg.funcNameHash, ...msg.params);
        }
    }
    /// Player message handlers ///
    handleUserSpawnedMessage() {
        if (this.context.currentRoom === undefined)
            return;
        for (const model of this.context.currentRoom.models.values()) {
            for (const script of model.scripts.values()) {
                script.onPlayerSpawned();
            }
        }
        this.context.flushMessages();
    }
    handlePeerAddedMessages(msg) {
        if (this.context.currentRoom === undefined)
            return;
        for (const model of this.context.currentRoom.models.values()) {
            for (const script of model.scripts.values()) {
                script.onClientConnect(msg.clientId);
            }
        }
        this.context.flushMessages();
    }
    handlePeerRemovedMessage(msg) {
        for (const [avatarUid, clientId] of this.peersInCurrentRoom.entries()) {
            if (clientId !== msg.clientId)
                continue;
            this.peersInCurrentRoom.delete(avatarUid);
            if (this.context.currentRoom === undefined)
                continue;
            for (const model of this.context.currentRoom.models.values()) {
                for (const script of model.scripts.values()) {
                    script.onPlayerLeavesRoom(avatarUid, clientId);
                }
            }
        }
        if (this.context.currentRoom === undefined)
            return;
        for (const model of this.context.currentRoom.models.values()) {
            for (const script of model.scripts.values()) {
                script.onClientDisconnect(msg.clientId);
            }
        }
        this.context.flushMessages();
    }
    /// Interaction message handlers ///
    handleSelectEventMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.uid);
        if (model === undefined)
            return;
        const clientId = this.resolveClientIdByControllerUid(msg.controllerUid);
        for (const script of model.scripts.values()) {
            script.onSelect(msg.isSelected, msg.controllerUid, clientId);
        }
        this.context.flushMessages();
    }
    handleModelClickedMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.uid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            script.onClick(msg);
        }
        this.context.flushMessages();
    }
    handleAnimationClipFinished(message) {
        const model = this.context.getModelInCurrentRoom(message.uid);
        if (!model)
            return;
        for (const script of model.scripts.values()) {
            script.onAnimationClipFinished(message);
        }
        this.context.flushMessages();
    }
    handleAvatarPeerRoomChangedMessage(msg) {
        if (this.context.currentRoom === undefined)
            return;
        const clientId = usersService.getClientIdByAvatarUid(msg.uid);
        const isInCurrentRoom = msg.roomDepthDiff === 0;
        const wasInCurrentRoom = this.peersInCurrentRoom.has(msg.uid);
        if (isInCurrentRoom === wasInCurrentRoom)
            return;
        if (isInCurrentRoom) {
            this.peersInCurrentRoom.set(msg.uid, clientId);
            for (const model of this.context.currentRoom.models.values()) {
                for (const script of model.scripts.values()) {
                    script.onPlayerEntersRoom(msg.uid, clientId);
                }
            }
        }
        else {
            this.peersInCurrentRoom.delete(msg.uid);
            for (const model of this.context.currentRoom.models.values()) {
                for (const script of model.scripts.values()) {
                    script.onPlayerLeavesRoom(msg.uid, clientId);
                }
            }
        }
        this.context.flushMessages();
    }
    handleRemoteModelClickedMessage(msg) {
        if (!this.context.isRoomHost)
            return;
        const model = this.context.getModelInCurrentRoom(msg.uid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            script.onRemoteClick(msg);
        }
        this.context.flushMessages();
    }
    handleControllerButtonMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onButton(msg.uid, msg.input, msg.buttonType);
            }
        }
        this.context.flushMessages();
    }
    handleControllerAxisMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onInputAxis(msg.uid, msg.axisType, msg.value);
            }
        }
        this.context.flushMessages();
    }
    handleInputFlaVRDefaultActionMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                switch (msg.action) {
                    case InputFlaVRDefaultActionType.Primary:
                        script.onPrimaryButton(msg.inputUid);
                        break;
                    case InputFlaVRDefaultActionType.Secondary:
                        script.onSecondaryButton(msg.inputUid);
                        break;
                    case InputFlaVRDefaultActionType.Tertiary:
                        script.onTertiaryButton(msg.inputUid);
                        break;
                    case InputFlaVRDefaultActionType.Quaternary:
                        script.onQuaternaryButton(msg.inputUid);
                        break;
                }
            }
        }
    }
    /// Model message handlers ///
    handleModelDestroyed(model, _destroyPermanently, meta) {
        this.context.destroyScripts(model.getUID(), meta.timeStamp);
    }
    handleModelTransformUpdated(model, localTransform) {
        const modelData = this.context.getModelInCurrentRoom(model.getUID());
        if (modelData === undefined)
            return;
        const globalTransform = this.tempGlobalTransform.copy(model.getWorldTransform());
        for (const script of modelData.scripts.values()) {
            script.onTransformUpdate(localTransform, globalTransform);
        }
        this.context.flushMessages();
    }
    handleRenderVisibilityMessage(msg) {
        const model = this.context.models.get(msg.uid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            script.setVisible(msg.visible);
        }
        this.context.flushMessages();
    }
    /// Life cycle message handlers ///
    handleLayoutCreationFinishedMessage(msg) {
        this.context.initializeRoomScripts();
        this.context.onLayoutCreationFinished(msg.uid, msg.isFirstCreation);
        this.context.flushMessages();
    }
    handleLayoutUpdatedMessage(_msg, meta) {
        for (const room of this.context.rooms.values()) {
            room.onLayoutUpdated();
        }
        this.context.scriptManager.onLayouterChanged();
        for (const uid of this.context.models.keys()) {
            this.context.destroyScripts(uid, meta.timeStamp);
        }
    }
    handleRoomChangedMessage(msg) {
        this.peersInCurrentRoom.clear();
        this.context.onChangeRoom(msg.uid);
        this.context.flushMessages();
    }
    resolveClientIdByControllerUid(controllerUid) {
        var _a, _b, _c;
        const localController = (_a = peerService.peerMgr) === null || _a === void 0 ? void 0 : _a.localControllers.get(controllerUid);
        if (localController !== undefined)
            return localController.ownerId;
        for (const controller of (_c = (_b = peerService.peerMgr) === null || _b === void 0 ? void 0 : _b.peersControllers.values()) !== null && _c !== void 0 ? _c : []) {
            if (controller.controllerUid === controllerUid) {
                return controller.ownerId;
            }
        }
        return undefined;
    }
    handleWebXRPresentingMessage() {
        if (this.context.currentRoom === undefined)
            return;
        for (const model of this.context.currentRoom.models.values()) {
            for (const script of model.scripts.values()) {
                script.onWebXrPresentingChanged();
            }
        }
        this.context.flushMessages();
    }
    /// Pickup message handlers ///
    handleOnPickupModelMessage(msg) {
        // Call onPickup for the respective model
        const modelData = this.context.getModelInCurrentRoom(msg.uid);
        const model = modelPool.getModel(msg.uid);
        if (modelData !== undefined) {
            for (const script of modelData.scripts.values()) {
                script.onPickup(msg.controllerUid, false);
            }
        }
        // Propagate event recursive to children
        if (model !== undefined)
            this.applyPickupToChildren(model, msg);
        // Call onOtherPickup for all models
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onOtherPickup(msg.uid, msg.controllerUid, false);
            }
        }
    }
    handleOnDropModelMessage(msg) {
        // Call onDrop on the respective model
        const model = this.context.getModelInCurrentRoom(msg.modelUid);
        if (model !== undefined) {
            for (const script of model.scripts.values()) {
                script.onDrop(msg.controllerUid);
            }
        }
        // Call onOtherDrop for all models
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onOtherDrop(msg.modelUid, msg.controllerUid);
            }
        }
    }
    handlePickupGrabbableMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.uid);
        if (model !== undefined) {
            for (const script of model.scripts.values()) {
                script.onGrabPickup();
            }
        }
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onOtherGrabPickup(msg.uid);
            }
        }
        this.context.flushMessages();
    }
    handleReleaseGrabbableMessage(msg) {
        const model = this.context.getModelInCurrentRoom(msg.uid);
        if (model !== undefined) {
            for (const script of model.scripts.values()) {
                script.onGrabReleased();
            }
        }
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onOtherGrabReleased(msg.uid);
            }
        }
        this.context.flushMessages();
    }
    /// Controller message handlers ///
    handleControllerConnectedMessage(msg) {
        if (this.context.currentRoom === undefined)
            return;
        for (const model of this.context.currentRoom.models.values()) {
            for (const script of model.scripts.values()) {
                script.onXrControllerConnected(msg.uid, msg.hand);
            }
        }
    }
    /// Collision message handlers ///
    handleOnEnterContactsMessage(msg) {
        for (const contact of msg.contacts) {
            const modelA = this.context.getModelInCurrentRoom(contact.collisionShapeAUID);
            if (modelA !== undefined) {
                for (const script of modelA.scripts.values()) {
                    script.onEnterContact(contact.collisionShapeBUID);
                }
            }
            const modelB = this.context.getModelInCurrentRoom(contact.collisionShapeBUID);
            if (modelB !== undefined) {
                for (const script of modelB.scripts.values()) {
                    script.onEnterContact(contact.collisionShapeAUID);
                }
            }
        }
        this.context.flushMessages();
    }
    handleOnLeaveContactsMessage(msg) {
        for (const contact of msg.contacts) {
            const modelA = this.context.getModelInCurrentRoom(contact.collisionShapeAUID);
            if (modelA !== undefined) {
                for (const script of modelA.scripts.values()) {
                    script.onLeaveContact(contact.collisionShapeBUID);
                }
            }
            const modelB = this.context.getModelInCurrentRoom(contact.collisionShapeBUID);
            if (modelB !== undefined) {
                for (const script of modelB.scripts.values()) {
                    script.onLeaveContact(contact.collisionShapeAUID);
                }
            }
        }
        this.context.flushMessages();
    }
    handleChangeRoomSupervisionMessage(msg) {
        this.context.isRoomHost = msg.enable;
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onHostChanged(msg.enable);
            }
        }
        this.context.flushMessages();
    }
    /// Meta data facet handlers ///
    handlePersistentDataFacet(uid, facet) {
        const model = this.context.models.get(uid);
        // ToDo if not found save for loading?
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            script.onUpdatePersistentVariables(facet);
        }
    }
    handleRemoveMetaDataFacet(uid) {
        const model = this.context.models.get(uid);
        if (model === undefined)
            return;
        for (const script of model.scripts.values()) {
            script.onUpdatePersistentVariables();
        }
    }
    /// Training message handlers ///
    handleTrainingVideoStateChangedMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onTrainingVideoStateChanged(msg.trainingVideoId, msg.newState);
            }
        }
        this.context.flushMessages();
    }
    handleTrainingStageChangedMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onTrainingStageChanged(msg.oldStageId, msg.newStageId);
            }
        }
        this.context.flushMessages();
    }
    handleTrainingFinishedMessage(msg) {
        for (const model of this.context.getModelsInCurrentRoomIterator()) {
            for (const script of model.scripts.values()) {
                script.onTrainingFinished(msg.isRevisitMode);
            }
        }
        this.context.flushMessages();
    }
    /// Private pickup helper methods ///
    applyPickupToChildren(model, msg) {
        for (const child of model.getChildren()) {
            const modelOfChild = this.context.getModelInCurrentRoom(child.getUID());
            if (modelOfChild === undefined)
                continue;
            for (const script of modelOfChild.scripts.values()) {
                script.onPickup(msg.controllerUid, true);
            }
            this.applyPickupToChildren(child, msg);
        }
    }
    waitForScriptsCreation(modelUid, includeDescendants, timeoutMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const timeoutAt = Date.now() + timeoutMs;
            while (Date.now() <= timeoutAt) {
                const status = this.getScriptsCreationStatus(modelUid, includeDescendants);
                if (status.ok)
                    return;
                yield new Promise(resolve => setTimeout(resolve, 10));
            }
            const finalStatus = this.getScriptsCreationStatus(modelUid, includeDescendants);
            throw new Error(`[scripting] Timed out waiting for script creation: ${finalStatus.reason}`);
        });
    }
    waitForScriptsActivation(modelUid, includeDescendants, timeoutMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const timeoutAt = Date.now() + timeoutMs;
            while (Date.now() <= timeoutAt) {
                const status = this.getScriptsActivationStatus(modelUid, includeDescendants);
                if (status.ok)
                    return;
                yield new Promise(resolve => setTimeout(resolve, 10));
            }
            const finalStatus = this.getScriptsActivationStatus(modelUid, includeDescendants);
            throw new Error(`[scripting] Timed out waiting for script activation: ${finalStatus.reason}`);
        });
    }
    activateScriptsLocally(modelUid, includeDescendants) {
        const rootModel = modelPool.getModel(modelUid);
        if (rootModel === undefined) {
            throw new Error(`[scripting] Cannot activate scripts because model ${String(modelUid)} does not exist`);
        }
        const models = [];
        rootModel.traverse(model => { models.push(model); }, true);
        const relevantModels = includeDescendants ? models : [rootModel];
        for (const model of relevantModels) {
            const scriptIds = this.getScriptIdsFromFacets(model);
            if (scriptIds.length === 0)
                continue;
            const modelData = this.context.getModelInCurrentRoom(model.getUID());
            if (modelData === undefined)
                continue;
            for (const scriptId of scriptIds) {
                const script = modelData.scripts.get(scriptId);
                if (script === undefined)
                    continue;
                script.syncExecutionState(script.autoExecute, true);
            }
        }
    }
    getScriptsCreationStatus(modelUid, includeDescendants) {
        const rootModel = modelPool.getModel(modelUid);
        if (rootModel === undefined) {
            return { ok: false, reason: `model ${String(modelUid)} does not exist` };
        }
        const models = [];
        rootModel.traverse(model => { models.push(model); }, true);
        const relevantModels = includeDescendants ? models : [rootModel];
        const missingScripts = [];
        for (const model of relevantModels) {
            const scriptIds = this.getScriptIdsFromFacets(model);
            if (scriptIds.length === 0)
                continue;
            const modelData = this.context.getModelInCurrentRoom(model.getUID());
            if (modelData === undefined) {
                missingScripts.push(`${model.getName()}:modelData missing`);
                continue;
            }
            for (const scriptId of scriptIds) {
                const script = modelData.scripts.get(scriptId);
                if (script === undefined) {
                    missingScripts.push(`${model.getName()}:${scriptId}:script missing`);
                    continue;
                }
                if (script.scriptInstance === undefined) {
                    missingScripts.push(`${model.getName()}:${scriptId}:loading`);
                }
            }
        }
        if (missingScripts.length === 0) {
            return { ok: true, reason: "" };
        }
        return { ok: false, reason: missingScripts.join(", ") };
    }
    getScriptsActivationStatus(modelUid, includeDescendants) {
        const rootModel = modelPool.getModel(modelUid);
        if (rootModel === undefined) {
            return { ok: false, reason: `model ${String(modelUid)} does not exist` };
        }
        const models = [];
        rootModel.traverse(model => { models.push(model); }, true);
        const relevantModels = includeDescendants ? models : [rootModel];
        const missingScripts = [];
        for (const model of relevantModels) {
            const expectedScriptIds = this.getScriptIdsFromFacets(model);
            if (expectedScriptIds.length === 0)
                continue;
            const modelData = this.context.getModelInCurrentRoom(model.getUID());
            if (modelData === undefined) {
                missingScripts.push(`${model.getName()}:modelData missing`);
                continue;
            }
            for (const scriptId of expectedScriptIds) {
                const script = modelData.scripts.get(scriptId);
                if (script === undefined) {
                    missingScripts.push(`${model.getName()}:${scriptId}:script missing`);
                    continue;
                }
                if (!this.isScriptActiveInLifecycle(script)) {
                    missingScripts.push(`${model.getName()}:${scriptId}:${this.getScriptActivationStateReason(script)}`);
                }
            }
        }
        if (missingScripts.length === 0) {
            return { ok: true, reason: "" };
        }
        return { ok: false, reason: missingScripts.join(", ") };
    }
    resetModelScripts(modelUids, includeDescendants) {
        return __awaiter(this, void 0, void 0, function* () {
            const models = new Map();
            for (const modelUid of modelUids) {
                const rootModel = modelPool.getModel(modelUid);
                if (rootModel === undefined)
                    continue;
                if (includeDescendants) {
                    rootModel.traverse(model => { models.set(model.getUID(), model); }, true);
                }
                else {
                    models.set(rootModel.getUID(), rootModel);
                }
            }
            const resetPromises = [];
            const resetScripts = [];
            for (const model of models.values()) {
                const modelData = this.context.getModelInCurrentRoom(model.getUID());
                if (modelData === undefined || modelData.scripts.size === 0)
                    continue;
                const scriptInfos = this.getScriptInfosFromFacets(model);
                for (const script of modelData.scripts.values()) {
                    resetScripts.push(script);
                    resetPromises.push(script.reset(scriptInfos.get(script.scriptId)));
                }
            }
            yield Promise.all(resetPromises);
            return resetScripts;
        });
    }
    waitForScriptsInitialization(scripts, timeoutMs) {
        return __awaiter(this, void 0, void 0, function* () {
            // Only scripts that are registered in an active, laid out room get initialized by the room update loop
            const isPending = (script) => script.isDestroyed !== true &&
                script.isRegisteredInRoom &&
                script.room.isActive &&
                script.room.isLayoutCreationFinished &&
                !script.isInitialized;
            const timeoutAt = Date.now() + timeoutMs;
            while (Date.now() <= timeoutAt) {
                if (!scripts.some(isPending))
                    return;
                yield new Promise(resolve => setTimeout(resolve, 10));
            }
            const pendingScripts = scripts.filter(isPending).map(script => `${String(script.scriptName)}@${String(script.modelUID)}`);
            throw new Error(`[scripting] Timed out waiting for script reset: ${pendingScripts.join(", ")}`);
        });
    }
    getScriptInfosFromFacets(model) {
        const scriptInfos = new Map();
        const scriptFacet = model.getFacet(ScriptFacet);
        if (scriptFacet !== undefined) {
            scriptInfos.set(scriptFacet.scriptId, {
                scriptId: scriptFacet.scriptId,
                properties: scriptFacet.properties,
                autoExecute: scriptFacet.autoExecute,
                active: scriptFacet.active
            });
        }
        const scriptComposeFacet = model.getFacet(ScriptComposeFacet);
        if (scriptComposeFacet !== undefined) {
            for (const scriptInfo of scriptComposeFacet.scripts) {
                scriptInfos.set(scriptInfo.scriptId, scriptInfo);
            }
        }
        return scriptInfos;
    }
    getScriptIdsFromFacets(model) {
        const scriptIds = new Set();
        const scriptFacet = model.getFacet(ScriptFacet);
        if (scriptFacet !== undefined) {
            scriptIds.add(scriptFacet.scriptId);
        }
        const scriptComposeFacet = model.getFacet(ScriptComposeFacet);
        if (scriptComposeFacet !== undefined) {
            for (const script of scriptComposeFacet.scripts) {
                scriptIds.add(script.scriptId);
            }
        }
        return Array.from(scriptIds);
    }
    isScriptActiveInLifecycle(script) {
        return (script.scriptInstance !== undefined &&
            script.isInitialized &&
            script.isRegisteredInRoom &&
            script.room.isActive);
    }
    getScriptActivationStateReason(script) {
        if (script.scriptInstance === undefined)
            return "loading";
        if (!script.isInitialized)
            return "created";
        if (!script.isRegisteredInRoom)
            return "initialized";
        if (!script.room.isActive)
            return "started";
        return "inactive";
    }
}
__decorate([
    FacetHandler(ScriptComposeFacet)
], ScriptModule.prototype, "handleScriptComposeFacet", null);
__decorate([
    FacetHandler(ScriptFacet)
], ScriptModule.prototype, "handleScriptFacet", null);
__decorate([
    FacetRemoveHandler(ScriptFacet)
], ScriptModule.prototype, "handleRemoveFacetFromModelMessage", null);
__decorate([
    MessageRequestHandler(RequestPredefinedModelScriptMessage)
], ScriptModule.prototype, "handleRequestPredefinedModelScriptMessage", null);
__decorate([
    MessageRequestHandler(AwaitScriptsActivationRequestMessage)
], ScriptModule.prototype, "handleAwaitScriptsActivationRequestMessage", null);
__decorate([
    MessageRequestHandler(ResetModelScriptsRequestMessage)
], ScriptModule.prototype, "handleResetModelScriptsRequestMessage", null);
__decorate([
    MessageHandler(ScriptChangedMessage)
], ScriptModule.prototype, "handleScriptChangedMessage", null);
__decorate([
    MessageRequestHandler(RPCScriptMessage)
], ScriptModule.prototype, "handleRPCScriptMessage", null);
__decorate([
    MessageHandler(ScriptRPCMessage)
], ScriptModule.prototype, "handleScriptRPCMessage", null);
__decorate([
    MessageHandler(ScriptRPCOnlyHostMessage)
], ScriptModule.prototype, "handleScriptRPCOnlyHostMessage", null);
__decorate([
    MessageHandler(UserSpawnedMessage)
], ScriptModule.prototype, "handleUserSpawnedMessage", null);
__decorate([
    MessageHandler(PeerAddedMessage)
], ScriptModule.prototype, "handlePeerAddedMessages", null);
__decorate([
    MessageHandler(PeerRemovedMessage)
], ScriptModule.prototype, "handlePeerRemovedMessage", null);
__decorate([
    MessageHandler(ModelSelectMessage)
], ScriptModule.prototype, "handleSelectEventMessage", null);
__decorate([
    MessageHandler(ModelClickedMessage)
], ScriptModule.prototype, "handleModelClickedMessage", null);
__decorate([
    MessageHandler(AnimationClipFinishedMessage)
], ScriptModule.prototype, "handleAnimationClipFinished", null);
__decorate([
    MessageHandler(AvatarPeerRoomChangedMessage)
], ScriptModule.prototype, "handleAvatarPeerRoomChangedMessage", null);
__decorate([
    MessageHandler(RemoteModelClickedMessage)
], ScriptModule.prototype, "handleRemoteModelClickedMessage", null);
__decorate([
    MessageHandler(ControllerButtonMessage)
], ScriptModule.prototype, "handleControllerButtonMessage", null);
__decorate([
    MessageHandler(ControllerAxisMessage)
], ScriptModule.prototype, "handleControllerAxisMessage", null);
__decorate([
    MessageHandler(InputFlaVRDefaultActionMessage)
], ScriptModule.prototype, "handleInputFlaVRDefaultActionMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], ScriptModule.prototype, "handleModelDestroyed", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], ScriptModule.prototype, "handleModelTransformUpdated", null);
__decorate([
    MessageHandler(RenderVisibilityMessage)
], ScriptModule.prototype, "handleRenderVisibilityMessage", null);
__decorate([
    MessageHandler(LayoutCompleteMessage)
], ScriptModule.prototype, "handleLayoutCreationFinishedMessage", null);
__decorate([
    MessageHandler(LayoutUpdatedMessage)
], ScriptModule.prototype, "handleLayoutUpdatedMessage", null);
__decorate([
    MessageHandler(RoomChangedMessage)
], ScriptModule.prototype, "handleRoomChangedMessage", null);
__decorate([
    MessageHandler(WebXRPresentingMessage)
], ScriptModule.prototype, "handleWebXRPresentingMessage", null);
__decorate([
    MessageHandler(OnPickupModelMessage)
], ScriptModule.prototype, "handleOnPickupModelMessage", null);
__decorate([
    MessageHandler(OnDropModelMessage)
], ScriptModule.prototype, "handleOnDropModelMessage", null);
__decorate([
    MessageHandler(PickupGrabbableMessage)
], ScriptModule.prototype, "handlePickupGrabbableMessage", null);
__decorate([
    MessageHandler(ReleaseGrabbableMessage)
], ScriptModule.prototype, "handleReleaseGrabbableMessage", null);
__decorate([
    MessageHandler(ControllerConnectedMessage)
], ScriptModule.prototype, "handleControllerConnectedMessage", null);
__decorate([
    MessageHandler(OnEnterContactsMessage)
], ScriptModule.prototype, "handleOnEnterContactsMessage", null);
__decorate([
    MessageHandler(OnLeaveContactsMessage)
], ScriptModule.prototype, "handleOnLeaveContactsMessage", null);
__decorate([
    MessageHandler(ChangeRoomSupervisionMessage)
], ScriptModule.prototype, "handleChangeRoomSupervisionMessage", null);
__decorate([
    FacetHandler(PersistentDataFacet)
], ScriptModule.prototype, "handlePersistentDataFacet", null);
__decorate([
    FacetRemoveHandler(MetaDataFacet)
], ScriptModule.prototype, "handleRemoveMetaDataFacet", null);
__decorate([
    MessageHandler(TrainingVideoStateChangedMessage)
], ScriptModule.prototype, "handleTrainingVideoStateChangedMessage", null);
__decorate([
    MessageHandler(TrainingStageChangedMessage)
], ScriptModule.prototype, "handleTrainingStageChangedMessage", null);
__decorate([
    MessageHandler(TrainingFinishedMessage)
], ScriptModule.prototype, "handleTrainingFinishedMessage", null);
